import React, { Suspense, useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { checkinAPI } from '../../services/api';
import { useAuth } from '../../auth/AuthContext';
import { homePathFor } from '../../auth/roles';
import { paths } from '../../routes/paths';
import { errMsg } from '../admin/ui';
import PageLoader from '../../components/PageLoader';
import Dropdown from '../../components/Dropdown';
import { BackIcon, ExitIcon, withSearch } from './staffParts';
import logoDark from '../../assets/logo.png';
import logoLight from '../../assets/logo-light.png';
import './staffMode.css';

// Staff mode (Figma "Staff check-in (web)"): the frame around every door screen: logo and "Staff mode" badge, a Back
// button, the event dropdown and "Exit staff mode". A staff member is an ordinary user who switches this on from their
// account menu; "Exit staff mode" returns them to the normal site. Each screen is its own route (see routes/paths.js);
// the event is part of the URL, and this shell shares that event and its door counts with the screens through the outlet.
export default function StaffShell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = useMatch(`${paths.staff.root}/:eventId/*`)?.params.eventId || '';
  const [events, setEvents] = useState(null);
  const [info, setInfo] = useState(null); // { event, counts } of the event in the URL
  const [error, setError] = useState('');

  useEffect(() => {
    checkinAPI.events().then(setEvents).catch((e) => { setEvents([]); setError(errMsg(e, 'Could not load your events')); });
  }, []);

  useEffect(() => {
    setInfo(null);
    if (eventId) checkinAPI.summary(eventId).then(setInfo).catch((e) => setError(errMsg(e)));
  }, [eventId]);

  // which screen this is (hooks are called unconditionally, then used)
  const onScanResult = useMatch(paths.staff.scanResult());
  const onScan = useMatch(paths.staff.scan());
  const onAttendee = useMatch(paths.staff.attendee());
  const onManual = useMatch(paths.staff.manual());

  // one step up from the screen being shown (the home screen has none: it has "Exit staff mode")
  const q = new URLSearchParams(location.search).get('q') || '';
  let back = null;
  if (eventId) {
    if (onScanResult) back = paths.staff.scan(eventId);
    else if (onScan) back = paths.staff.event(eventId);
    else if (onAttendee) back = withSearch(paths.staff.manual(eventId), q);
    else if (onManual) back = paths.staff.event(eventId);
  }

  const green = !!onScanResult && location.state?.result?.result === 'checked_in';
  const theme = green ? 'green' : onManual || onAttendee ? 'light' : 'dark';

  // a URL for an event this person cannot check in (or one that is over) goes to the first one they can
  if (events && eventId && !events.some((e) => e.id === eventId)) return <Navigate to={paths.staff.root} replace />;

  return (
    <div className={`sm sm-${theme}`}>
      <header className="sm-top">
        <div className="sm-top-left">
          <Link to={eventId ? paths.staff.event(eventId) : paths.staff.root} className="sm-brand" title="Back to check-in">
            <img src={logoDark} alt="Events Ignite" className="sm-logo-on-light" />
            <img src={logoLight} alt="Events Ignite" className="sm-logo-on-dark" />
            <span className="sm-badge">Staff mode</span>
          </Link>
        </div>
        <div className="sm-top-right">
          {events && events.length > 0 && (
            <div className="sm-select sm-dd">
              <Dropdown
                label="Event"
                value={eventId}
                options={[...(eventId ? [] : [{ value: '', label: 'Choose an event' }]), ...events.map((e) => ({ value: e.id, label: e.title }))]}
                onChange={(id) => id && navigate(paths.staff.event(id))}
              />
            </div>
          )}
          <button type="button" className="sm-exit" onClick={() => navigate(homePathFor(user))}><ExitIcon />Exit staff mode</button>
        </div>
      </header>

      {back && <div className="sm-backrow"><Link to={back} className="sm-backbtn"><BackIcon />Back</Link></div>}

      <main className="sm-main">
        <Suspense fallback={<PageLoader />}>
          <Outlet context={{ events, eventId, info, setInfo, error }} />
        </Suspense>
      </main>
    </div>
  );
}
